import { Check, Copy, Lock, Files } from "lucide-react";
import { Button } from "@/fileshare/components/ui/button";
import { Input } from "@/fileshare/components/ui/input";
import { Label } from "@/fileshare/components/ui/label";
import { formatBytes } from "@/fileshare/lib/format";
import ShareActions from "@/fileshare/components/share/ShareActions";

export default function UploadResult({ result, shareUrl, copied, onCopy, onReset }) {
  const fileCount = result.file_count || 1;
  const title = fileCount === 1 ? result.files?.[0]?.original_filename || "1 file" : `${fileCount} files`;

  return (
    <div data-testid="upload-result">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-md bg-emerald-50 flex items-center justify-center">
          <Check className="h-5 w-5 text-emerald-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Your transfer is ready</h3>
          <p className="text-sm text-slate-500" data-testid="upload-result-summary">
            {fileCount} {fileCount === 1 ? "file" : "files"} · {formatBytes(result.total_size || 0)}
          </p>
        </div>
      </div>

      {result.files?.length > 1 && (
        <div className="mb-5 max-h-36 overflow-y-auto rounded-md border border-slate-100 divide-y divide-slate-50" data-testid="upload-result-files">
          {result.files.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="text-slate-700 truncate flex items-center gap-2 min-w-0">
                <Files className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                <span className="truncate">{f.original_filename}</span>
              </span>
              <span className="text-xs text-slate-400 shrink-0 ml-3">{formatBytes(f.file_size)}</span>
            </div>
          ))}
        </div>
      )}

      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Shareable link</Label>
      <div className="mt-2 flex gap-2">
        <Input
          data-testid="share-link-input"
          readOnly
          value={shareUrl}
          className="bg-slate-50 border-slate-200 text-slate-700 font-medium"
        />
        <Button
          data-testid="copy-link-button"
          onClick={onCopy}
          className="bg-blue-500 hover:bg-blue-600 text-white shrink-0 gap-1.5"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>

      <ShareActions url={shareUrl} title={title} />

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {result.has_password && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700"><Lock className="h-3 w-3" /> Password protected</span>
        )}
        {result.max_downloads != null && (
          <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Max {result.max_downloads} downloads</span>
        )}
      </div>

      <Button
        data-testid="upload-another-button"
        onClick={onReset}
        variant="outline"
        className="mt-6 w-full border-slate-200 text-slate-700 hover:bg-slate-50"
      >
        Upload more files
      </Button>
    </div>
  );
}
